import { useEffect, useRef, useState } from 'react';
import type { DisplaySettingsView, ListenerGrant, ListenerState, ScheduleMatch, StreamView } from '@ai-voice/contracts';
import { formatActivationCountdown } from '../activation';
import { ApiError, api, normalizeLatencyMs } from '../api';
import { getLanguage, getLanguageTimeZone, t, tError } from '../i18n';
import { ActivationPurchasePanel } from './ActivationPurchasePanel';
import { getDeviceId, getSavedNickname, saveNickname } from '../device';
import { useScheduleNameLocalizer } from '../schedule-i18n';
import { scrollingMessageDurationSeconds } from '../marquee';
import {
  enterListenerRoom,
  getSpeakerList,
  setSpeaker
} from '../trtc';

type ListenerController = Awaited<ReturnType<typeof enterListenerRoom>>;
const TERMINAL_ERROR_CODES = new Set(['SESSION_ENDED', 'STREAM_OFFLINE', 'DEVICE_BANNED', 'UNAUTHORIZED']);
const LISTENER_SESSION_KEY = 'ai-voice-listener-session';

type StoredListenerSession = {
  grant: ListenerGrant;
  code: string;
  nickname: string;
};

function readStoredListenerSession(): StoredListenerSession | null {
  try {
    const raw = sessionStorage.getItem(LISTENER_SESSION_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as StoredListenerSession;
    if (!value.grant?.sessionId || !value.grant?.sessionSecret || !value.code || !value.nickname) return null;
    return value;
  } catch {
    sessionStorage.removeItem(LISTENER_SESSION_KEY);
    return null;
  }
}

function clearStoredListenerSession(): void {
  sessionStorage.removeItem(LISTENER_SESSION_KEY);
}

export function ListenerPanel() {
  const [restoredSession] = useState(readStoredListenerSession);
  const [code, setCode] = useState(restoredSession?.code ?? '');
  const [nickname, setNickname] = useState(restoredSession?.nickname ?? getSavedNickname());
  const [grant, setGrant] = useState<ListenerGrant | null>(null);
  const [waitingReason, setWaitingReason] = useState<'not-started' | 'stop-grace' | undefined>(restoredSession?.grant.waitingReason);
  const [connection, setConnection] = useState<'connected' | 'connecting' | 'reconnecting' | 'waiting' | 'disconnected'>('disconnected');
  const [remoteAudio, setRemoteAudio] = useState(false);
  const [paused, setPaused] = useState(false);
  const [volume, setVolume] = useState(100);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [speakerId, setSpeakerId] = useState('');
  const [error, setError] = useState('');
  const [nicknameError, setNicknameError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [resumeUntil, setResumeUntil] = useState('');
  const [schedule, setSchedule] = useState<ScheduleMatch[]>([]);
  const [displaySettings, setDisplaySettings] = useState<DisplaySettingsView | null>(null);
  const [streams, setStreams] = useState<StreamView[]>([]);
  const [streamSyncError, setStreamSyncError] = useState(false);
  const [streamSyncTimedOut, setStreamSyncTimedOut] = useState(false);
  const [switchTargetId, setSwitchTargetId] = useState('');
  const [switching, setSwitching] = useState(false);
  const [scheduleClockMs, setScheduleClockMs] = useState(Date.now());
  const localizeScheduleName = useScheduleNameLocalizer();
  const controllerRef = useRef<ListenerController | null>(null);
  const grantRef = useRef<ListenerGrant | null>(null);
  const pausedRef = useRef(false);
  const volumeRef = useRef(100);
  const terminalRef = useRef(false);
  const everConnectedRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectingRef = useRef(false);
  const waitingRef = useRef(false);
  const disconnectTimerRef = useRef<number | null>(null);
  const controlFailureCountRef = useRef(0);
  const manualLeaveRef = useRef(false);
  const autoResumeRef = useRef(false);
  const activationExpiredRef = useRef(false);
  const switchingRef = useRef(false);
  const lastKnownStreamRef = useRef<{ streamId: string; stream: StreamView } | null>(null);

  useEffect(() => {
    if (!restoredSession) return;
    let active = true;
    setNotice(t('正在恢复上次收听...'));
    void api.listenerState(restoredSession.grant.sessionId, restoredSession.grant.sessionSecret)
      .then((next) => {
        if (!active) return;
        if (next.status === 'ended') {
          clearStoredListenerSession();
          setNotice(t('上次收听会话已结束。'));
          return;
        }
        const hydrated: ListenerGrant = {
          ...restoredSession.grant,
          streamId: next.streamId ?? restoredSession.grant.streamId,
          roomId: next.roomId ?? restoredSession.grant.roomId,
          userSig: next.userSig ?? restoredSession.grant.userSig,
          privateMapKey: next.privateMapKey ?? restoredSession.grant.privateMapKey,
          resumeUntil: next.resumeUntil ?? restoredSession.grant.resumeUntil,
          roomStatus: next.status === 'waiting' ? 'waiting' : 'active'
        };
        if (next.status === 'waiting') {
          const reason = next.waitingReason ?? (next.resumeUntil ? 'stop-grace' : 'not-started');
          waitingRef.current = true;
          setWaitingReason(reason);
          setConnection('waiting');
          setNotice(waitingNotice(reason));
        }
        grantRef.current = hydrated;
        setGrant(hydrated);
        setResumeUntil(hydrated.resumeUntil ?? '');
      })
      .catch(() => {
        if (active) {
          clearStoredListenerSession();
          setNotice(t('上次收听会话已失效，请重新进入。'));
        }
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!grant) {
      clearStoredListenerSession();
      return;
    }
    sessionStorage.setItem(LISTENER_SESSION_KEY, JSON.stringify({ grant, code, nickname: nickname.trim() }));
  }, [grant, code, nickname]);

  useEffect(() => {
    if (!grant || controllerRef.current || waitingRef.current || grant.roomStatus === 'waiting') return;
    let active = true;
    grantRef.current = grant;
    setConnection('connecting');
    void openRoom(grant)
      .then((controller) => {
        if (!active) {
          void controller.stop().catch(() => undefined);
          return;
        }
        controllerRef.current = controller;
        setConnection('connected');
        setError('');
        setNotice(t('已恢复收听。'));
      })
      .catch(() => {
        if (active && !terminalRef.current) scheduleReconnect();
      });
    return () => { active = false; };
  }, [grant?.sessionId, grant?.roomId]);
  useEffect(() => {
    grantRef.current = grant;
  }, [grant]);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    volumeRef.current = volume;
    if (!grant) return;
    void controllerRef.current?.setVolume(volume).catch(() => undefined);
  }, [volume, grant]);

  useEffect(() => () => {
    terminalRef.current = true;
    clearReconnectTimer();
    void controllerRef.current?.stop().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!grant) return;
    const heartbeatTimer = window.setInterval(async () => {
      const current = grantRef.current;
      if (!current || terminalRef.current || switchingRef.current) return;
      const requestSessionId = current.sessionId;
      try {
        const next = await api.heartbeat({
          sessionId: current.sessionId,
          sessionSecret: current.sessionSecret,
          latencyMs: normalizeLatencyMs(latencyMs),
          playing: !pausedRef.current
        });
        if (grantRef.current?.sessionId !== requestSessionId) return;
        if (next.resumeUntil) setResumeUntil(next.resumeUntil);
        controlFailureCountRef.current = 0;
        syncGrantFromServerState(next);
        applyServerStatus(next.status, next.status === 'paused', next.waitingReason);
      } catch (cause) {
        if (grantRef.current?.sessionId !== requestSessionId) return;
        if (isTerminalError(cause)) {
          if (cause instanceof ApiError && cause.code === 'SESSION_ENDED') {
            const current = grantRef.current;
            if (current) {
              try {
                const endedState = await api.listenerState(current.sessionId, current.sessionSecret);
                if (endedState.status === 'ended') {
                  await handleEndedState(endedState.endReason);
                  return;
                }
              } catch { /* Fall through to the terminal message. */ }
            }
          }
          if (cause instanceof ApiError && cause.code === 'STREAM_OFFLINE' && await tryAutoResume()) return;
          await endWithMessage(messageOf(cause));
        } else {
          controlFailureCountRef.current += 1;
          if (controlFailureCountRef.current >= 3) setNotice(t('控制服务网络不稳定，正在自动重试...'));
        }
      }
    }, 15_000);

    const stateTimer = window.setInterval(async () => {
      const current = grantRef.current;
      if (!current || terminalRef.current || switchingRef.current) return;
      const requestSessionId = current.sessionId;
      try {
        const next = await api.listenerState(current.sessionId, current.sessionSecret);
        if (grantRef.current?.sessionId !== requestSessionId) return;
        if (next.status === 'ended') {
          await handleEndedState(next.endReason);
          return;
        }
        if (next.resumeUntil) setResumeUntil(next.resumeUntil);
        syncGrantFromServerState(next);
        applyServerStatus(next.status, next.status === 'paused', next.waitingReason);
      } catch (cause) {
        if (grantRef.current?.sessionId === requestSessionId && isTerminalError(cause)) await endWithMessage(messageOf(cause));
      }
    }, 5_000);

    return () => {
      window.clearInterval(heartbeatTimer);
      window.clearInterval(stateTimer);
    };
  }, [grant]);

  useEffect(() => {
    let active = true;
    const loadSchedule = () => {
      void api.schedule()
        .then((items) => { if (active) setSchedule(items); })
        .catch(() => undefined);
      void api.streams()
        .then((items) => {
          if (!active) return;
          setStreams(items);
          setStreamSyncError(false);
        })
        .catch(() => { if (active) setStreamSyncError(true); });
    };
    loadSchedule();
    const refreshTimer = window.setInterval(loadSchedule, 10_000);
    const clockTimer = window.setInterval(() => setScheduleClockMs(Date.now()), 1000);
    return () => {
      active = false;
      window.clearInterval(refreshTimer);
      window.clearInterval(clockTimer);
    };
  }, []);


  useEffect(() => {
    let active = true;
    const loadDisplaySettings = () => {
      void api.displaySettings()
        .then((settings) => { if (active) setDisplaySettings(settings); })
        .catch(() => undefined);
    };
    loadDisplaySettings();
    const timer = window.setInterval(loadDisplaySettings, 10_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!grant?.activationExpiresAt) {
      activationExpiredRef.current = false;
      return;
    }
    if (new Date(grant.activationExpiresAt).getTime() > scheduleClockMs) {
      activationExpiredRef.current = false;
      return;
    }
    if (activationExpiredRef.current) return;
    activationExpiredRef.current = true;
    void endWithMessage(t('激活码已过期，请重新购买或联系主控获取。'));
  }, [grant?.activationExpiresAt, scheduleClockMs]);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refreshSessionNow();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    setSwitchTargetId(grant?.streamId ?? '');
  }, [grant?.streamId]);

  useEffect(() => {
    if (!grant) return;
    void getSpeakerList().then((items) => {
      setSpeakers(items);
      setSpeakerId(items[0]?.deviceId ?? '');
    });
  }, [grant]);

  function clearReconnectTimer(): void {
    if (reconnectTimerRef.current == null) return;
    window.clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
  }
  function clearDisconnectTimer(): void {
    if (disconnectTimerRef.current == null) return;
    window.clearTimeout(disconnectTimerRef.current);
    disconnectTimerRef.current = null;
  }

  function armDisconnectReconnect(delay = 10_000): void {
    if (waitingRef.current || terminalRef.current || disconnectTimerRef.current != null) return;
    setConnection(everConnectedRef.current ? 'reconnecting' : 'connecting');
    disconnectTimerRef.current = window.setTimeout(() => {
      disconnectTimerRef.current = null;
      if (!waitingRef.current && !terminalRef.current) scheduleReconnect();
    }, delay);
  }

  function scheduleReconnect(): void {
    if (!grantRef.current || terminalRef.current || reconnectingRef.current || reconnectTimerRef.current != null) return;
    const attempt = reconnectAttemptsRef.current;
    if (attempt >= 5) {
      void endWithMessage(t('连接多次恢复失败，请重新取得激活码。'));
      return;
    }
    const delay = [2000, 3000, 5000, 8000, 13000][attempt] ?? 13000;
    setConnection('connecting');
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      reconnectAttemptsRef.current = attempt + 1;
      void reconnectRoom();
    }, delay);
  }

  function isRecoverableEndReason(reason?: string): boolean {
    return Boolean(reason && ['resume-timeout', 'stream-stopped', 'stop-grace-expired'].includes(reason));
  }

  async function tryAutoResume(): Promise<boolean> {
    const cleanNickname = nickname.trim();
    if (manualLeaveRef.current || autoResumeRef.current || !code || !cleanNickname) return false;
    autoResumeRef.current = true;
    setNotice(t('检测到临时中断，正在自动恢复...'));
    const previousController = controllerRef.current;
    controllerRef.current = null;
    await previousController?.stop().catch(() => undefined);
    try {
      const nextGrant = await api.redeem({ code, deviceId: getDeviceId(), nickname: cleanNickname, streamId: grantRef.current?.streamId });
      terminalRef.current = false;
      if (nextGrant.roomStatus === 'waiting') {
        const reason = nextGrant.waitingReason ?? 'stop-grace';
        waitingRef.current = true;
        grantRef.current = nextGrant;
        setGrant(nextGrant);
        setWaitingReason(reason);
        setResumeUntil(nextGrant.resumeUntil ?? '');
        setConnection('waiting');
        setNotice(waitingNotice(reason));
        return true;
      }
      waitingRef.current = false;
      setWaitingReason(undefined);
      grantRef.current = nextGrant;
      const controller = await openRoom(nextGrant);
      controllerRef.current = controller;
      setGrant(nextGrant);
      setResumeUntil(nextGrant.resumeUntil ?? '');
      setConnection('connected');
      setError('');
      setNotice(t('已自动恢复收听。'));
      return true;
    } catch {
      return false;
    } finally {
      autoResumeRef.current = false;
    }
  }

  async function handleEndedState(endReason?: string): Promise<void> {
    if (manualLeaveRef.current) return;
    if (isRecoverableEndReason(endReason) && await tryAutoResume()) return;
    await endWithMessage(t('会话已结束，如需继续收听请向主控索取新的激活码。'));
  }

  async function handleRoomKick(reason: string): Promise<void> {
    const current = grantRef.current;
    if (!current || manualLeaveRef.current || switchingRef.current || terminalRef.current || reconnectingRef.current) return;

    // Give the server a moment to persist an admin action or recoverable end reason.
    await new Promise((resolve) => window.setTimeout(resolve, 350));
    if (grantRef.current?.sessionId !== current.sessionId || terminalRef.current) return;

    try {
      const next = await api.listenerState(current.sessionId, current.sessionSecret);
      if (grantRef.current?.sessionId !== current.sessionId) return;
      if (next.status === 'ended') {
        if (next.endReason === 'kicked') {
          terminalRef.current = true;
          await endWithMessage(t('你已被主控移出语音室。'));
          return;
        }
        if (next.endReason === 'banned') {
          terminalRef.current = true;
          await endWithMessage(t('该设备已被封禁，请联系主控。'));
          return;
        }
        await handleEndedState(next.endReason);
        return;
      }
      syncGrantFromServerState(next);
      applyServerStatus(next.status, next.status === 'paused', next.waitingReason);
      if (next.status === 'paused' || next.status === 'waiting') return;
    } catch {
      // If the state check fails during a network interruption, retry the room connection.
    }

    if (reason === 'banned') {
      terminalRef.current = true;
      await endWithMessage(t('该设备已被封禁，请联系主控。'));
      return;
    }
    setNotice(t('连接中断，正在恢复...'));
    scheduleReconnect();
  }

  function syncGrantFromServerState(next: ListenerState): ListenerGrant | null {
    const current = grantRef.current;
    if (!current) return null;
    const streamId = next.streamId ?? current.streamId;
    const roomId = next.roomId ?? current.roomId;
    const streamChanged = streamId !== current.streamId || roomId !== current.roomId;
    const updated: ListenerGrant = {
      ...current,
      streamId,
      roomId,
      userSig: next.userSig ?? current.userSig,
      privateMapKey: next.privateMapKey ?? current.privateMapKey,
      resumeUntil: next.resumeUntil ?? current.resumeUntil,
      roomStatus: next.status === 'waiting' ? 'waiting' : 'active'
    };
    grantRef.current = updated;
    if (streamChanged) {
      setGrant(updated);
      setSwitchTargetId(updated.streamId);
    }
    return updated;
  }

  async function refreshSessionNow(): Promise<void> {
    const current = grantRef.current;
    if (!current || terminalRef.current || switchingRef.current) return;
    const requestSessionId = current.sessionId;
    try {
      const next = await api.listenerState(current.sessionId, current.sessionSecret);
      if (grantRef.current?.sessionId !== requestSessionId) return;
      if (next.status === 'ended') {
        await handleEndedState(next.endReason);
        return;
      }
      if (next.resumeUntil) setResumeUntil(next.resumeUntil);
      syncGrantFromServerState(next);
      applyServerStatus(next.status, next.status === 'paused', next.waitingReason);
      if (next.status === 'active' && waitingRef.current) waitingRef.current = false;
    } catch (cause) {
      if (grantRef.current?.sessionId === requestSessionId && isTerminalError(cause)) await handleEndedState();
    }
  }
  async function openRoom(current: ListenerGrant): Promise<ListenerController> {
    const isCurrentSession = () => grantRef.current?.sessionId === current.sessionId;
    const controller = await enterListenerRoom({
      ...current,
      volume: volumeRef.current,
      onConnectionState: (next) => {
        if (switchingRef.current || !isCurrentSession()) return;
        if (next === 'connected') {
          everConnectedRef.current = true;
          reconnectAttemptsRef.current = 0;
          clearReconnectTimer();
          clearDisconnectTimer();
          setConnection('connected');
          setError('');
          return;
        }
        if (waitingRef.current) {
          setConnection('waiting');
          return;
        }
        if (next === 'connecting') {
          setConnection(everConnectedRef.current ? 'reconnecting' : 'connecting');
          return;
        }
        if (everConnectedRef.current) {
          armDisconnectReconnect(10_000);
        } else {
          setConnection('disconnected');
        }
      },
      onNetworkQuality: (latency) => { if (!switchingRef.current && isCurrentSession()) setLatencyMs(latency); },
      onRemoteAudio: (available) => {
        if (switchingRef.current || !isCurrentSession()) return;
        setRemoteAudio(available);
        if (available) {
          everConnectedRef.current = true;
          reconnectAttemptsRef.current = 0;
          clearReconnectTimer();
          clearDisconnectTimer();
          setConnection('connected');
        }
      },
      onError: () => {
        if (switchingRef.current || !isCurrentSession()) return;
        if (terminalRef.current || waitingRef.current) return;
        setNotice(t('音频连接波动，正在等待自动恢复...'));
        armDisconnectReconnect(2000);
      },
      onKickedOut: (reason) => {
        if (switchingRef.current || !isCurrentSession()) return;
        void handleRoomKick(reason);
      },
      onCommand: (command) => {
        if (switchingRef.current || !isCurrentSession()) return;
        if (command.targetUserId && command.targetUserId !== current.userId) return;
        const shouldPause = command.type === 'pause';
        setPaused(shouldPause);
        pausedRef.current = shouldPause;
        setNotice(shouldPause ? t('主控已暂停你的播放。') : t('主控已恢复播放。'));
        void controllerRef.current?.setPaused(shouldPause);
      }
    });
    return controller;
  }

  async function reconnectRoom(force = false): Promise<void> {
    const current = grantRef.current;
    if (!current || terminalRef.current || reconnectingRef.current || (waitingRef.current && !force)) return;
    reconnectingRef.current = true;
    setConnection('connecting');
    setNotice(t('连接中断，正在恢复...'));
    const previous = controllerRef.current;
    controllerRef.current = null;
    await previous?.stop().catch(() => undefined);

    try {
      const controller = await openRoom(current);
      controllerRef.current = controller;
      await controller.setVolume(volumeRef.current).catch(() => undefined);
      await controller.setPaused(pausedRef.current).catch(() => undefined);
      setNotice(t('连接已恢复。'));
      setError('');
    } catch {
      setConnection('disconnected');
      if (reconnectAttemptsRef.current >= 5) {
        await endWithMessage(t('连接多次恢复失败，请重新取得激活码。'));
      } else {
        scheduleReconnect();
      }
    } finally {
      reconnectingRef.current = false;
    }
  }

  async function endWithMessage(message: string): Promise<void> {
    terminalRef.current = true;
    clearReconnectTimer();
    reconnectAttemptsRef.current = 0;
    reconnectingRef.current = false;
    await controllerRef.current?.stop().catch(() => undefined);
    controllerRef.current = null;
    grantRef.current = null;
    clearStoredListenerSession();
    setGrant(null);
    setRemoteAudio(false);
    setConnection('disconnected');
    setError(message);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const cleanNickname = nickname.trim();
    if (!cleanNickname) {
      setNicknameError(t('请填写昵称后再进入'));
      return;
    }

    setBusy(true);
    setError('');
    setNicknameError('');
    setNotice('');
    terminalRef.current = false;
    manualLeaveRef.current = false;
    everConnectedRef.current = false;
    reconnectAttemptsRef.current = 0;
    clearReconnectTimer();
    saveNickname(cleanNickname);
    setNickname(cleanNickname);
    try {
      const nextGrant = await api.redeem({ code, deviceId: getDeviceId(), nickname: cleanNickname, streamId: grantRef.current?.streamId });
      if (nextGrant.roomStatus === 'waiting') {
        const reason = nextGrant.waitingReason ?? 'not-started';
        waitingRef.current = true;
        grantRef.current = nextGrant;
        setGrant(nextGrant);
        setWaitingReason(reason);
        setResumeUntil(nextGrant.resumeUntil ?? '');
        setConnection('waiting');
        setNotice(waitingNotice(reason));
        void api.displaySettings().then(setDisplaySettings).catch(() => undefined);
        return;
      }
      grantRef.current = nextGrant;
      setGrant(nextGrant);
      setWaitingReason(undefined);
      setResumeUntil(nextGrant.resumeUntil ?? '');
      setConnection('connecting');
      setNotice(t('正在进入语音室...'));
      void api.displaySettings().then(setDisplaySettings).catch(() => undefined);
    } catch (cause) {
      grantRef.current = null;
      await controllerRef.current?.stop().catch(() => undefined);
      controllerRef.current = null;
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  function applyServerStatus(
    status: 'active' | 'paused' | 'waiting' | 'ended',
    nextPaused: boolean,
    reason?: 'not-started' | 'stop-grace'
  ): void {
    if (status === 'ended') return;
    if (status === 'waiting') {
      const nextReason = reason ?? 'stop-grace';
      waitingRef.current = true;
      setWaitingReason(nextReason);
      clearReconnectTimer();
      clearDisconnectTimer();
      setConnection('waiting');
      setRemoteAudio(false);
      setNotice(waitingNotice(nextReason));
      return;
    }
    const wasWaiting = waitingRef.current;
    waitingRef.current = false;
    setWaitingReason(undefined);
    if (wasWaiting && !reconnectingRef.current) void reconnectRoom(true);
    setPaused(nextPaused);
    pausedRef.current = nextPaused;
    void controllerRef.current?.setPaused(nextPaused);
  }

  async function togglePaused(): Promise<void> {
    const next = !paused;
    setPaused(next);
    pausedRef.current = next;
    await controllerRef.current?.setPaused(next).catch(() => undefined);
    setNotice(next ? t('已在本机暂停播放。') : t('已恢复播放。'));
  }

  async function switchStream(): Promise<void> {
    const current = grantRef.current;
    if (!current || !switchTargetId || switchTargetId === current.streamId || switchingRef.current) return;
    switchingRef.current = true;
    setSwitching(true);
    setError('');
    let nextGrant: ListenerGrant | null = null;
    try {
      nextGrant = await api.redeem({
        code,
        deviceId: getDeviceId(),
        nickname: nickname.trim(),
        streamId: switchTargetId
      });
      let nextController: ListenerController | null = null;
      grantRef.current = nextGrant;
      if (nextGrant.roomStatus !== 'waiting') {
        everConnectedRef.current = false;
        nextController = await openRoom(nextGrant);
      }
      const previousController = controllerRef.current;
      controllerRef.current = null;
      await previousController?.stop().catch(() => undefined);

      controllerRef.current = nextController;
      setGrant(nextGrant);
      setSwitchTargetId(nextGrant.streamId);
      setWaitingReason(nextGrant.waitingReason);
      setResumeUntil(nextGrant.resumeUntil ?? '');
      setPaused(false);
      pausedRef.current = false;
      waitingRef.current = nextGrant.roomStatus === 'waiting';
      setConnection(nextGrant.roomStatus === 'waiting' ? 'waiting' : 'connected');
      setRemoteAudio(nextGrant.roomStatus !== 'waiting' && Boolean(nextController));
      setNotice(nextGrant.roomStatus === 'waiting' ? waitingNotice(nextGrant.waitingReason ?? 'not-started') : t('已切换到新的比赛。'));
    } catch (cause) {
      if (nextGrant && current.streamId) {
        try {
          const rollback = await api.redeem({
            code,
            deviceId: getDeviceId(),
            nickname: nickname.trim(),
            streamId: current.streamId
          });
          grantRef.current = rollback;
          const rollbackController = rollback.roomStatus === 'waiting' ? null : await openRoom(rollback);
          controllerRef.current = rollbackController;
          setGrant(rollback);

          setResumeUntil(rollback.resumeUntil ?? '');
          setConnection(rollback.roomStatus === 'waiting' ? 'waiting' : 'connected');
        } catch {
          await endWithMessage(t('切换失败，原房间也无法恢复，请重新进入语音室。'));
          return;
        }
      }
      setError(t('切换比赛失败：{message}', { message: messageOf(cause) }));
    } finally {
      switchingRef.current = false;
      setSwitching(false);
    }
  }
  async function leave(): Promise<void> {
    manualLeaveRef.current = true;
    const current = grantRef.current;
    if (current) await api.leave(current.sessionId, current.sessionSecret).catch(() => undefined);
    await endWithMessage('');
    setNotice(t('已退出语音室。'));
    void api.displaySettings().then(setDisplaySettings).catch(() => undefined);
  }

  async function switchSpeaker(nextId: string): Promise<void> {
    setSpeakerId(nextId);
    try {
      await setSpeaker(nextId);
    } catch (cause) {
      setError(messageOf(cause));
    }
  }

  const activeStreams = streams.filter((stream) => stream.status === 'active');
  const switchOptions = activeStreams;
  const currentStream = grant
    ? streams.find((stream) => stream.sessionId === grant.streamId)
    : undefined;
  const cachedStream = grant && lastKnownStreamRef.current?.streamId === grant.streamId
    ? lastKnownStreamRef.current.stream
    : undefined;
  const listeningMatch = currentStream?.match ?? cachedStream?.match;

  useEffect(() => {
    if (currentStream?.match) {
      lastKnownStreamRef.current = { streamId: currentStream.sessionId, stream: currentStream };
    }
  }, [currentStream?.sessionId, currentStream?.matchId]);

  useEffect(() => {
    if (!grant || listeningMatch) {
      setStreamSyncTimedOut(false);
      return;
    }
    setStreamSyncTimedOut(false);
    const timer = window.setTimeout(() => setStreamSyncTimedOut(true), 10_000);
    return () => window.clearTimeout(timer);
  }, [grant?.streamId, grant?.roomStatus, listeningMatch?.id]);

  const listeningState = !grant
    ? undefined
    : listeningMatch
      ? (grant.roomStatus === 'waiting' ? 'waiting' : 'ready')
      : grant.roomStatus === 'waiting'
        ? 'waiting'
        : streamSyncError || streamSyncTimedOut
          ? 'stale'
          : 'syncing';

  if (grant) {
    return (
      <div className="listener-page">
        <ScheduleShowcase matches={schedule} now={scheduleClockMs} compact listeningMatchId={currentStream?.matchId ?? cachedStream?.matchId} fallbackMatch={listeningMatch} listeningState={listeningState} localizeName={localizeScheduleName} />
        <ScrollingMessage message={displaySettings?.scrollingMessage} />
        <div className="listener-shell">
          <section className="listener-card playing-card">
          <div className="listener-orb-wrap">
            <div className={`listener-orb ${paused ? 'paused' : 'playing'}`}><span /><span /><span /></div>
            <div className="listener-live-status">
              <div className={`status-dot ${connection === 'connected' ? 'live' : 'offline'}`}>{connectionLabel(connection)}</div>
              {displaySettings && <span className="virtual-online-badge">{t('在线 {count} 人', { count: displaySettings.virtualOnlineCount })}</span>}
            </div>
          </div>
          <span className="eyebrow">{t('正在收听')}</span>
          <h1>{connection === 'waiting'
            ? t('等待音源接入中')
            : paused ? t('播放已暂停') : remoteAudio ? t('声音正在播放') : t('等待声音到来')}</h1>
          {grant.activationExpiresAt && (
            <div className="activation-countdown">
              {t('激活码剩余有效期')} <strong>{formatActivationCountdown(grant.activationExpiresAt, scheduleClockMs)}</strong>
            </div>
          )}

          {notice && <div className="alert info">{notice}</div>}
          {error && <div className="alert error">{error}</div>}

          <div className="listener-controls">
            <label>
              <span>{t('本机音量')} <strong>{volume}%</strong></span>
              <input type="range" min={0} max={100} value={volume} onChange={(event) => setVolume(Number(event.target.value))} />
            </label>
            {speakers.length > 1 && (
              <label>
                <span>{t('播放设备')}</span>
                <select value={speakerId} onChange={(event) => void switchSpeaker(event.target.value)}>
                  {speakers.map((speaker) => <option key={speaker.deviceId} value={speaker.deviceId}>{speaker.label || t('默认播放设备')}</option>)}
                </select>
              </label>
            )}
          </div>

          {switchOptions.length > 0 && (
            <div className="stream-switcher">
              <label>
                <span>{t('切换比赛')}</span>
                <select value={switchTargetId || grant.streamId} onChange={(event) => setSwitchTargetId(event.target.value)}>
                  {switchOptions.map((stream) => (
                    <option key={stream.sessionId} value={stream.sessionId}>
                      {stream.match ? `${localizeScheduleName(stream.match.homeTeam)} VS ${localizeScheduleName(stream.match.awayTeam)}` : t('其他直播')} · {stream.match ? scheduleBroadcastLabel(stream.match.broadcastType) : ''}{stream.sessionId === grant.streamId ? t('（当前收听）') : ''}
                    </option>
                  ))}
                </select>
              </label>
              <button className="secondary" disabled={!switchTargetId || switchTargetId === grant.streamId || switching} onClick={() => void switchStream()}>
                {switching ? t('正在切换...') : t('切换到此场')}
              </button>
            </div>
          )}
          <div className="listener-meta">
            <span>{t('网络延迟')} <strong>{latencyMs == null ? '--' : `${latencyMs} ms`}</strong></span>
            {connection !== 'waiting' && <span>{t('恢复窗口到')} <strong>{resumeUntil ? formatTime(resumeUntil) : '--'}</strong></span>}
          </div>

          <div className="action-row center">
            <button className="primary" onClick={() => void togglePaused()}>{paused ? t('恢复播放') : t('暂停播放')}</button>
            <button className="ghost danger-text" onClick={() => void leave()}>{t('退出语音室')}</button>
          </div>
          <small className="privacy-note">{t('主控可以暂停播放、踢出或封禁设备。会话不会给予下载或录制权限。')}</small>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="listener-page">
      <ScheduleShowcase matches={schedule} now={scheduleClockMs} preview liveMatches={activeStreams.map((stream) => stream.match).filter((match): match is ScheduleMatch => Boolean(match))} localizeName={localizeScheduleName} />
      <ScrollingMessage message={displaySettings?.scrollingMessage} />
      <div className="listener-shell">
        <section className="listener-card">
        <span className="eyebrow">{t('听众入口')}</span>
        <h1>{t('请输入你的六位激活码')}</h1>
        {displaySettings && <div className="virtual-online-public">{t('在线 {count} 人', { count: displaySettings.virtualOnlineCount })}</div>}
        <p className="muted">{t('每张激活码最多绑定 3 台设备，同一时间仅限 1 台在线；关闭页面或主动退出后，可重新输入同一激活码继续收听。')}</p>
        <form onSubmit={submit} className="stack">
          <label>{t('你的昵称（必填）')}<input value={nickname} minLength={1} maxLength={24} aria-required="true" onChange={(event) => { setNickname(event.target.value); setNicknameError(''); }} placeholder={t('例如：小王')} required /></label>

          {nicknameError && <div className="alert error">{nicknameError}</div>}
          <label>{t('六位激活码')}<input className="code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" required /></label>
          <button className="primary large" disabled={busy || code.length !== 6 || !nickname.trim()}>{busy ? t('正在连接...') : t('进入语音室')}</button>
        </form>
        {error && <div className="alert error">{error}</div>}
        <ActivationPurchasePanel displaySettings={displaySettings} onCodeReady={setCode} />

        </section>
      </div>
    </div>
  );
}

function ScrollingMessage({ message }: { message?: string }) {
  const text = message?.trim() ?? '';
  if (!text) return null;
  const duration = scrollingMessageDurationSeconds(text);
  return (
    <section className="scrolling-message" aria-label={t('滚动字幕')} key={text}>
      <div className="scrolling-message-track" style={{ animationDuration: `${duration}s` }}>
        <span>{text}</span>
        <span aria-hidden="true">{text}</span>
      </div>
    </section>
  );
}

function ScheduleShowcase({ matches, now, compact = false, preview = false, listeningMatchId, fallbackMatch, liveMatches = [], listeningState, localizeName }: { matches: ScheduleMatch[]; now: number; compact?: boolean; preview?: boolean; listeningMatchId?: string; fallbackMatch?: ScheduleMatch; liveMatches?: ScheduleMatch[]; listeningState?: 'syncing' | 'waiting' | 'stale' | 'ready'; localizeName: (value: string) => string }) {
  const visible = matches
    .filter((match) => new Date(match.startsAt).getTime() + 2 * 60 * 60 * 1000 > now)
    .sort((left, right) => left.startsAt.localeCompare(right.startsAt));
  const current = visible.find((match) => {
    const startsAt = new Date(match.startsAt).getTime();
    return startsAt <= now && startsAt + 2 * 60 * 60 * 1000 > now;
  });
  const next = visible.find((match) => new Date(match.startsAt).getTime() > now);
  const livePreview = Array.from(new Map(
    liveMatches.filter((match) => Boolean(match?.id)).map((match) => [match.id, match])
  ).values());
  const listeningMatch = listeningMatchId
    ? visible.find((match) => match.id === listeningMatchId) ?? fallbackMatch
    : undefined;

  if (preview) {
    const previewMatches = livePreview.length
      ? livePreview
      : [current, next].filter((match): match is ScheduleMatch => Boolean(match));
    if (!previewMatches.length) {
      return <section className="schedule-showcase empty"><span>{t('暂无赛程')}</span></section>;
    }
    return (
      <section className="schedule-preview">
        {previewMatches.map((match) => {
          const live = livePreview.some((item) => item.id === match.id);
          const active = live || current?.id === match.id;
          return (
            <div className={`schedule-preview-card ${active ? 'current' : ''}`} key={match.id}>
              <div className={`schedule-state ${active ? 'live' : ''}`}>{live ? t('正在直播') : current?.id === match.id ? t('正在进行') : t('下一场')}</div>
              <div className="schedule-preview-time">{formatMatchClock(match.startsAt)}</div>
              <strong>{localizeName(match.homeTeam)} <span>VS</span> {localizeName(match.awayTeam)}</strong>
              <small>{localizeName(match.league)} · {formatMatchDate(match.startsAt)} · {scheduleBroadcastLabel(match.broadcastType)}</small>
            </div>
          );
        })}
      </section>
    );
  }

  if (!listeningMatch && listeningState === 'waiting') {
    return <section className={`schedule-showcase empty ${compact ? 'compact' : ''}`}><span>{t('等待开播')}</span></section>;
  }
  if (!listeningMatch && listeningState === 'stale') {
    return <section className={`schedule-showcase empty ${compact ? 'compact' : ''}`}><span>{t('赛程信息暂未同步，正在自动重试')}</span></section>;
  }
  if (!listeningMatch && listeningState === 'syncing') {
    return <section className={`schedule-showcase empty ${compact ? 'compact' : ''}`}><span>{t('正在同步当前直播赛程...')}</span></section>;
  }

  const featured = listeningMatch ?? current ?? next;
  if (!featured) {
    return <section className={`schedule-showcase empty ${compact ? 'compact' : ''}`}><span>{t('暂无赛程')}</span></section>;
  }

  return (
    <section className={`schedule-showcase ${compact ? 'compact' : ''}`}>
      <div className="schedule-featured">
        <div className={`schedule-state ${listeningState === 'waiting' ? '' : listeningMatch || current ? 'live' : ''}`}>{listeningState === 'waiting' ? t('等待开播') : listeningMatch ? t('正在收听') : current ? t('正在进行') : t('下一场直播')}</div>
        <div className="schedule-featured-time">{formatMatchClock(featured.startsAt)}</div>
        <div className="schedule-featured-teams">
          <strong>{localizeName(featured.homeTeam)}</strong><span>VS</span><strong>{localizeName(featured.awayTeam)}</strong>
        </div>
        <div className="schedule-featured-meta">{localizeName(featured.league)} · {formatMatchDate(featured.startsAt)} · {scheduleBroadcastLabel(featured.broadcastType)}</div>
      </div>
      <div className="schedule-upcoming">
        <div className="schedule-list-label">{t('近期赛程')}</div>
        {visible.slice(0, 6).map((match) => (
          <div className="schedule-list-row" key={match.id}>
            <time>{formatMatchClock(match.startsAt)}</time>
            <span>{localizeName(match.league)} · {scheduleBroadcastLabel(match.broadcastType)}</span>
            <strong>{localizeName(match.homeTeam)} <i>VS</i> {localizeName(match.awayTeam)}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}
function scheduleBroadcastLabel(value: string): string {
  return value === 'chinese' ? t('中文播报') : t('外语播报');
}

function formatMatchClock(value: string): string {
  return new Intl.DateTimeFormat(getLanguage() === 'zh' ? 'zh-CN' : getLanguage() === 'ru' ? 'ru-RU' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: getLanguageTimeZone() }).format(new Date(value));
}

function formatMatchDate(value: string): string {
  return new Intl.DateTimeFormat(getLanguage() === 'zh' ? 'zh-CN' : getLanguage() === 'ru' ? 'ru-RU' : 'en-US', { month: 'long', day: 'numeric', weekday: 'short', timeZone: getLanguageTimeZone() }).format(new Date(value));
}
function isTerminalError(cause: unknown): boolean {
  return cause instanceof ApiError && TERMINAL_ERROR_CODES.has(cause.code);
}

function connectionLabel(value: string): string {
  return ({ connected: t('已连接'), connecting: t('重连中'), waiting: t('等待中'), disconnected: t('未连接') } as Record<string, string>)[value] ?? value;
}

function waitingNotice(reason: 'not-started' | 'stop-grace'): string {
  return t('耐心等待，祝君好运！');
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat(getLanguage() === 'zh' ? 'zh-CN' : getLanguage() === 'ru' ? 'ru-RU' : 'en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: getLanguageTimeZone() }).format(new Date(value));
}

function messageOf(cause: unknown): string {
  if (cause instanceof ApiError) return tError(cause.code, cause.message);
  return cause instanceof Error ? cause.message : String(cause);
}
